import { Button, DialogActions, MenuItem, Stack, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useState, useRef, useEffect } from 'react';
import * as yup from 'yup';
import FormTextField from '../../../../components/FormTextField';
import { useTheme } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { buttonStyle2, dialogButtonCancel } from '../../../../utils/commonStyle';
import appapi from '../../../../api/apiClient';
import { setLoading } from '../../../../redux/features/appSlice';
import { showmessage } from '../../../../utils/toastr';
import FormSelectField from '../../../../components/FormSelectField';

const validationSchema = yup.object({
  displayName: yup.string('Display Name').min(3, 'Display Name should be atleast 3 characters').max(30, 'Maximum Character Limit 30').required(' '),
  traderName: yup.string('Trader Name').min(3, 'Trader Name should be atleast 3 characters').max(100, 'Maximum Character Limit 100').required(' '),
  traderAddress: yup.string('Trader Name').min(1, 'Trader Address should be atleast 3 characters').max(300, 'Maximum Character Limit 300').required(' '),
  countryName: yup.string('Country Name').required(' '),
  stateName: yup.string('State Name').required(' '),
  traderWebsite: yup
    .string('Website')
    .required(' ')
    .matches(/[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/gi, 'Enter valid url!'),
});

export default function AddEditTrader({ onModelHandleClose, modelOpen, onSuccess, trader, handleClose }) {
  const formRef = useRef();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { user, userId } = useSelector((state) => state.auth);
  const { countryList, traderList } = useSelector((state) => state.layout);

  const [countryId, setCountryId] = useState(null);
  const [stateList, setStateList] = useState([]);
  const [createdTraderId, setCreatedTraderId] = useState('');

  useEffect(() => {
    if (countryId) {
      getStateList();
    }
  }, [countryId]);

  useEffect(() => {
    if (trader?.countryName) {
      const idObj = countryList.filter((res) => res.countryName === trader?.countryName)[0];
      if (idObj) {
        setCountryId(idObj._id);
      }
    }
  }, [trader?.countryName]);

  const getStateList = async () => {
    const response = await appapi.get(`common/state/${countryId}`);
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        setStateList(data.response || []);
      } else {
        setStateList([]);
      }
    }
  };

  const handleAddTrader = async (values) => {
    dispatch(setLoading(true));
    const payload = values;
    const response = await appapi.post(`/trader`, payload);
    if (response.ok) {
      dispatch(setLoading(false));
      const data = response.data;
      if (data.status === '7400') {
        // showmessage(data.message);
        setCreatedTraderId(data?.response?._id);
        onSuccess(data?.response);
      } else {
        showmessage(data.message, 'error');
      }
    } else {
      dispatch(setLoading(false));
      showmessage(`Something Went Wrong!`, 'error', 2000);
    }
  };

  const handleEditTrader = async (values) => {
    dispatch(setLoading(true));
    const payload = values;
    const response = await appapi.put(`/trader/${trader._id}`, payload);
    if (response.ok) {
      dispatch(setLoading(false));
      const data = response.data;
      if (data.status === '7400') {
        onSuccess(data?.response);
        showmessage(data.message, 'success');
      } else {
        showmessage(data.message, 'error');
      }
    } else {
      dispatch(setLoading(false));
      showmessage(`Something Went Wrong!`, 'error', 2000);
    }
  };

  return (
    <Stack
      style={{ backgroundColor: 'white' }}
      flex={1}
    >
      <Formik
        innerRef={formRef}
        enableReinitialize={true}
        initialValues={{
          displayName: trader?.displayName || '',
          traderName: trader?.traderName || '',
          traderAddress: trader?.traderAddress || '',
          traderWebsite: trader?.traderWebsite || '',
          countryName: trader?.countryName || '',
          stateName: trader?.stateName || '',
          adminId: userId,
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          if (trader) {
            handleEditTrader(values);
          } else {
            handleAddTrader(values);
          }
        }}
      >
        {({ name, values, touched, errors, handleChange, handleBlur, isValid, dirty, setFieldValue }) => (
          <Form autoComplete="off">
            <Stack
              flex={1}
              style={{ backgroundColor: 'white', borderRadius: '8px' }}
              className="m-[15px] p-[16px]"
            >
              <Typography
                className="fw-medium ff-Roboto fs-14"
                style={{ marginBottom: '20px' }}
              >
                Company details
              </Typography>
              <Stack>
                <FormTextField
                  placeholder={'Display Name'}
                  outlineLabel={'Display Name'}
                  name="displayName"
                  requiredMark={true}
                  allowAlphaNumeric={true}
                  whiteSpace={true}
                  inputProps={{ maxLength: 101 }}
                />
              </Stack>
              <Stack>
                <FormTextField
                  placeholder={'Trader Name'}
                  outlineLabel={'Trader Name'}
                  name="traderName"
                  requiredMark={true}
                  allowAlphaNumeric={true}
                  whiteSpace={true}
                  inputProps={{ maxLength: 101 }}
                />
              </Stack>
              <Stack>
                <FormTextField
                  placeholder={'Address'}
                  outlineLabel={'Address'}
                  name="traderAddress"
                  requiredMark={true}
                  // onlyAlpha={true}
                  whiteSpace={true}
                  inputProps={{ maxLength: 301 }}
                />
              </Stack>
              <Stack>
                <FormSelectField
                  name="countryName"
                  label={'Country *'}
                  onSelect={(e) => {
                    setFieldValue('stateName', undefined);
                    const idObj = countryList.filter((res) => res.countryName === e)[0];
                    if (idObj) {
                      setCountryId(idObj._id);
                    }
                  }}
                >
                  {countryList?.map((item) => (
                    <MenuItem
                      key={item.countryName}
                      value={item.countryName}
                    >
                      <div style={{ textTransform: 'capitalize' }}>{item.countryName}</div>
                    </MenuItem>
                  ))}
                </FormSelectField>
              </Stack>
              <Stack>
                <FormSelectField
                  name="stateName"
                  label={'State *'}
                >
                  {stateList?.length === 0 && (
                    <MenuItem
                      disabled
                      value=""
                    >
                      <em>No Data Found</em>
                    </MenuItem>
                  )}
                  {stateList?.map((item) => (
                    <MenuItem
                      key={item.stateName}
                      value={item.stateName}
                    >
                      <div style={{ textTransform: 'capitalize' }}>{item.stateName}</div>
                    </MenuItem>
                  ))}
                </FormSelectField>
              </Stack>
              <Stack>
                <FormTextField
                  placeholder={'Website'}
                  outlineLabel={'Website'}
                  name="traderWebsite"
                  requiredMark={true}
                  whiteSpace={true}
                  inputProps={{ maxLength: 200 }}
                  lowerCase={true}
                />
              </Stack>
            </Stack>

            <DialogActions
              className="!mt-[68px] h-[66px] !justify-end p-[0px] "
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <Button
                sx={[
                  dialogButtonCancel,
                  {
                    color: theme.palette.primary.main,
                    borderColor: '#14285A33',
                    backgroundColor: '#14285A1A',
                    '&:hover': {
                      // backgroundColor: theme.palette.secondary.main,
                      // color: theme.palette.background.main,
                      // borderColor: theme.palette.secondary.main,
                    },
                  },
                ]}
                style={{ width: '128px', marginLeft: '80px', height: '40px' }}
                variant="outlined"
                onClick={() => {
                  handleClose();
                  //   setConfirmVal('');
                }}
              >
                CANCEL
              </Button>
              <Button
                sx={[
                  buttonStyle2,
                  {
                    color: theme.palette.background.main,
                    backgroundColor: theme.palette.primary.dark,
                    '&:hover': {
                      // backgroundColor: theme.palette.secondary.main,
                      // color: theme.palette.background.main,
                    },
                  },
                ]}
                style={{ width: '128px', height: '40px' }}
                disabled={!(dirty && isValid)}
                type="submit"
                variant="contained"
              >
                SAVE
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Stack>
  );
}
